import './styles/tailwind.css';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ScrollProvider } from './contexts/ScrollContext.js';
import AdminApp from './admin/AdminApp.js';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};
import NavigationBar from './components/NavigationBar.js';
import HomePage from './components/HomePage.js';
import ServicesPage from './components/ServicesPage.js';
import AboutPage from './components/AboutPage.js';
import ProcessesPage from './components/ProcessesPage.js';
import ContactPage from './components/ContactPage.js';
import EcoPracticesPage from './components/EcoPracticesPage.js';
import Footer from './components/Footer.js';
import PrivacyPolicyPage from './components/PrivacyPolicy.js';
import FAQPage from './components/FAQPage.js';
import RailroadPage from './components/RailroadPage.js';
import OilGasPage from './components/OilGasPage.js';
import MunicipalPage from './components/MunicipalPage.js';
import UtilityPage from './components/UtilityPage.js';
import LowerMainlandPage from './components/LowerMainlandPage.js';
import OkanaganPage from './components/OkanaganPage.js';
import InteriorBCPage from './components/InteriorBCPage.js';
import NorthernBCPage from './components/NorthernBCPage.js';
import KootenayPage from './components/KootenayPage.js';
import ChatbotWidget from './components/ChatbotWidget.js';

const AppContent = () => {
  const { pathname } = useLocation();
  const isPortal = pathname.startsWith('/portal') || pathname.startsWith('/admin');

  if (isPortal) {
    return <AdminApp />;
  }

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Boultbee Vegetation Management",
            "alternateName": "Boultbee Pest Control Ltd",
            "url": "https://www.weedmanager.com",
            "logo": "https://www.weedmanager.com/images/boultbee-logo.png",
            "foundingDate": "1984",
            "description": "Industrial and commercial herbicide application company serving British Columbia. Bare ground, turf, noxious weed, and aquatic vegetation management.",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1101 Spiller Road",
              "addressLocality": "Penticton",
              "addressRegion": "BC",
              "postalCode": "V2A 6J9",
              "addressCountry": "CA"
            },
            "telephone": "+1-250-492-0296",
            "areaServed": {
              "@type": "AdministrativeArea",
              "name": "British Columbia, Canada"
            },
            "memberOf": [
              {
                "@type": "Organization",
                "name": "Integrated Vegetation Management Association of BC (IVMA)"
              }
            ],
            "hasCredential": [
              { "@type": "EducationalOccupationalCredential", "credentialCategory": "Safety Certification", "name": "Avetta Certified" },
              { "@type": "EducationalOccupationalCredential", "credentialCategory": "Safety Compliance", "name": "WorkSafeBC Compliant" },
              { "@type": "EducationalOccupationalCredential", "credentialCategory": "Pesticide Certification", "name": "BC Industrial Vegetation & Noxious Weed Pesticide Applicator Certificate" }
            ],
            "sameAs": []
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Boultbee Vegetation Management",
            "image": "https://www.weedmanager.com/images/boultbee-logo.png",
            "url": "https://www.weedmanager.com",
            "telephone": "+1-250-492-0296",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1101 Spiller Road",
              "addressLocality": "Penticton",
              "addressRegion": "BC",
              "postalCode": "V2A 6J9",
              "addressCountry": "CA"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 49.4991,
              "longitude": -119.5937
            },
            "areaServed": [
              { "@type": "AdministrativeArea", "name": "British Columbia" },
              { "@type": "City", "name": "Penticton" },
              { "@type": "City", "name": "Kelowna" },
              { "@type": "City", "name": "Kamloops" },
              { "@type": "City", "name": "Vancouver" },
              { "@type": "City", "name": "Prince George" }
            ],
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
                "opens": "07:00",
                "closes": "17:00"
              }
            ],
            "knowsAbout": [
              "Industrial Vegetation Management",
              "Herbicide Application",
              "Noxious Weed Control",
              "Integrated Pest Management",
              "Railroad Vegetation Management",
              "Aquatic Vegetation Control"
            ]
          })}
        </script>
      </Helmet>
      <NavigationBar />
      <Routes>
        <Route path="/*" element={<HomePage />} />
        <Route path="/services/*" element={<ServicesPage />} />
        <Route path="/about-us/*" element={<AboutPage />} />
        <Route path="/processes/*" element={<ProcessesPage />} />
        <Route path="/contact/*" element={<ContactPage />} />
        <Route path="/ecopractices/*" element={<EcoPracticesPage />} />
        <Route path="/privacy-policy/*" element={<PrivacyPolicyPage />} />
        <Route path="/faq/*" element={<FAQPage />} />
        <Route path="/industries/railroad/*" element={<RailroadPage />} />
        <Route path="/industries/oil-gas/*" element={<OilGasPage />} />
        <Route path="/industries/municipal/*" element={<MunicipalPage />} />
        <Route path="/industries/utility/*" element={<UtilityPage />} />
        <Route path="/service-areas/lower-mainland/*" element={<LowerMainlandPage />} />
        <Route path="/service-areas/okanagan/*" element={<OkanaganPage />} />
        <Route path="/service-areas/interior-bc/*" element={<InteriorBCPage />} />
        <Route path="/service-areas/northern-bc/*" element={<NorthernBCPage />} />
        <Route path="/service-areas/kootenays/*" element={<KootenayPage />} />
      </Routes>
      <Footer />
      <ChatbotWidget />
    </>
  );
};

const App = () => {
  return (
    <ScrollProvider>
      <Router>
        <ScrollToTop />
        <AppContent />
      </Router>
    </ScrollProvider>
  );
};

export default App;